import { MouseEvent } from "react";

import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import { USER_ADS_ROUTE } from "@/common/static/routes.static";
import { cn } from "@/common/utils/css.utils";
import {
  UNAVAILABLE_VARIANT,
  UnavailableVariant,
} from "@/common/utils/sold-product.utils";
import { useAskSeller } from "@/components/ProductDetail/hooks/useAskSeller.hooks";
import useMerchant from "@/hooks/useMerchant";
import { Button } from "@/ui/cva/Button";

/**
 * The second action in the sold page's right rail: talk to the seller, or
 * go and see what else they have.
 *
 * The page previously carried the live PDP's whole seller block, which
 * renders `ProductDetailsInfoSellerContact`. That component disables
 * Contact seller on a sold item, so the sold page ended on a dead button
 * with no explanation of why it was dead, while the same block repeated
 * the seller's tenure and sales stats that the trust band above it had
 * already shown.
 *
 * The design review's answer is to make the CTA general rather than scoped
 * to the sold item. "Contact seller" on a marketplace of one-offs is a
 * reasonable thing to want after the piece is gone: the seller may have
 * something comparable, or be able to source one. That removes the need to
 * explain a disabled state, because there is none. The text link below it
 * goes to the seller's other listings, which is the real reason to click
 * through.
 *
 * Two cases still render no button, and both are the seller's own choice
 * rather than a decision this page makes: a seller who has turned contact
 * off (`disableContactSeller`), and the seller looking at their own sold
 * listing. The link to their other listings stays in both cases.
 *
 * Vacation mode deliberately does NOT disable this. On a live PDP that
 * flag stops a buyer starting a purchase conversation about an item they
 * expect to receive soon; here there is no purchase and no expectation,
 * and the message lands in the same inbox whenever the seller returns.
 *
 * The seller link is a button that navigates on click, not an anchor:
 * `/user-ads` is robots-disallowed, and an `<a>` to it on every sold page
 * still gets it discovered and indexed at scale (WHO-5477 follow-up).
 *
 * WHO-6094: a withdrawn listing (deleted by the seller, expired, banned)
 * renders no button either. The seller took the piece down, so a buyer
 * arriving from an old newsletter or their favourites should not open a
 * conversation about it. The link to their other listings stays.
 */

type SoldSellerActionsProps = {
  product: ProductBySlugNonNullable;
  variant?: UnavailableVariant;
  className?: string;
};

export const SoldSellerActions = ({
  product,
  variant = UNAVAILABLE_VARIANT.Sold,
  className,
}: SoldSellerActionsProps) => {
  const { t } = useTranslation();
  const router = useRouter();

  const { askSeller } = useAskSeller(product);
  const { id: userId } = useMerchant();

  const merchantId = product.merchant.id;
  const isProductOwner = merchantId === userId;
  const canContact =
    variant !== UNAVAILABLE_VARIANT.Withdrawn &&
    !isProductOwner &&
    !product.merchant.disableContactSeller;

  const handleContactSellerClick = async (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    await askSeller();
  };

  return (
    <div
      data-testid="sold-seller-actions"
      className={cn("flex flex-col gap-2", className)}
    >
      {canContact ? (
        <Button
          data-testid="sold-contact-seller"
          appearance="outlined"
          color="brand"
          onClick={handleContactSellerClick}
        >
          {t("pdp:pdp-seller-block-contact")}
        </Button>
      ) : null}

      <button
        type="button"
        data-testid="sold-seller-more-ads"
        className="self-start font-body text-sm text-brand-500 underline"
        onClick={() => router.push(`${USER_ADS_ROUTE}/${merchantId}`)}
      >
        {t("pdp:pdp-sold-seller-more", {
          defaultValue: "View more from this seller",
        })}
      </button>
    </div>
  );
};

export default SoldSellerActions;
